import Phaser from 'phaser';
import { applyLook, ensureMasks } from '../look/applyLook.js';
import { isDefaultLook } from '../look/lookCatalog.mjs';
import { HAIR_ART, driverKey } from '../look/hairArt.mjs';
import { preloader } from '../scenes/PreloadScene.js';
import { CAR_CONTENT_FRAC, CAR_SEATS, PLATE_BOX, PLATE_QUAD, carDisplayHeight, carDisplayHeightAt, carWheelOffset } from './carCatalog.mjs';

// One car, drawn the same way everywhere: the street, the dealer showroom, the garage.
// A Container so the caller moves/depths one object and the plate rides along with the body.
//
//   const car = createCarSprite(scene, 'panda', 'side', x, y, { plate: 'WA 1234' });
//   setCarFacing(car, 'right');   // side view only
//   setCarDepth(car, 5);
//   setCarGroundY(car, 632);      // side view: put the wheels on this line, at that y's size
//
// Two views, two jobs:
//   side  — the driving/parked silhouette, drawn so the BODY is the car's real height at
//           OUTDOOR_PX_PER_M (a Panda 154 px, a C8 123) — see carDisplayHeight — times the
//           y-perspective of the line its wheels stand on (see setCarGroundY)
//   front — the showroom portrait with a readable plate, drawn at the same relative heights
//           (the Q5 fills the 200 px the front view has always used, the rest come out
//           shorter than it instead of all being the same)
//
// The art faces LEFT, so `facing: 'left'` is the un-flipped sprite.

const FALLBACK_W = 300, FALLBACK_H = 150;   // stand-in body when the texture never loaded
const FALLBACK_COLOR = 0x9aa0a6;

// Plate geometry is authored against the source art's pixels (PLATE_BOX / PLATE_QUAD in
// carCatalog.mjs), so it has to be scaled by the same factor the body was, and anchored to
// the body's top-left corner — the image is centred in the container, so that corner is at
// (-displayWidth / 2, -displayHeight / 2).
//
// Two ways of drawing it:
//
//   addPlateMesh  the real one. None of these portraits is a dead-on front view — every
//                 plate is a slightly tilted, slightly tapered QUAD — so the text is drawn
//                 into a DynamicTexture and mapped onto that quad with a two-triangle Mesh.
//                 The lettering then leans and narrows exactly like the plate it sits on.
//   addPlateFlat  the fallback: one rotated Text in the middle of PLATE_BOX. Used under the
//                 Canvas renderer (Mesh is WebGL-only) and for any model without a quad.

const PLATE_SS = 2;        // supersample the plate texture: it is only ~10 world px tall
let plateTextureSeq = 0;

function plateStyle(px) {
  return {
    fontFamily: 'system-ui', fontStyle: 'bold',
    fontSize: `${Math.max(8, Math.round(px))}px`, color: '#000000',
  };
}

function addPlateFlat(scene, container, img, model, plate, scale) {
  const box = PLATE_BOX[model];
  if (!box) return null;
  const x = -img.displayWidth / 2 + box.x * scale;
  const y = -img.displayHeight / 2 + box.y * scale;
  const maxW = box.w * scale;

  const text = scene.add.text(x, y, String(plate), plateStyle(maxW * 0.28)).setOrigin(0.5);
  // Plates differ in length ("WA 1" vs "WA 12345"): shrink to fit rather than spill over
  // the white rectangle painted into the art.
  if (text.width > maxW) text.setScale(maxW / text.width);
  text.setAngle(box.angle ?? 0);
  container.add(text);
  return text;
}

function addPlateMesh(scene, container, img, model, plate, scale) {
  const quad = PLATE_QUAD[model];
  if (!quad || scene.renderer?.type !== Phaser.WEBGL || !scene.textures?.addDynamicTexture) return null;

  // The four corners in CONTAINER-local pixels: source px → body scale, off the top-left.
  const ox = -img.displayWidth / 2, oy = -img.displayHeight / 2;
  const corner = (k) => [ox + quad[k][0] * scale, oy + quad[k][1] * scale];
  const tl = corner('tl'), tr = corner('tr'), br = corner('br'), bl = corner('bl');
  const w = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]);
  const h = Math.max(Math.hypot(bl[0] - tl[0], bl[1] - tl[1]),
    Math.hypot(br[0] - tr[0], br[1] - tr[1]));
  if (!(w > 1 && h > 1)) return null;

  // Draw the text once, straight, into a texture the size of the plate (×2, because the
  // plate is tiny on screen and the mesh samples it with a linear filter).
  const texW = Math.round(w * PLATE_SS), texH = Math.round(h * PLATE_SS);
  const key = `plate_rt_${model}_${plateTextureSeq++}`;
  const tex = scene.textures.addDynamicTexture(key, texW, texH);
  if (!tex) return null;
  const text = scene.make.text({ x: 0, y: 0, text: String(plate), style: plateStyle(texH * 0.66) }, false)
    .setOrigin(0.5);
  const fit = Math.min(1, (texW * 0.9) / (text.width || 1));
  if (fit < 1) text.setScale(fit);
  tex.draw(text, texW / 2, texH / 2);
  text.destroy();

  const mesh = scene.add.mesh(0, 0, key);
  // Both triangles are wound the same way, but which way that IS depends on the quad, and a
  // back-face test would hide the plate on half the models. There is nothing behind it.
  mesh.hideCCW = false;
  // One vertex unit = one world pixel: `setOrtho(w, h)` with the Mesh's own w/h (the
  // renderer's size) makes transformCoordinatesLocal a no-op scale. Mesh y points UP, hence
  // the negated y's; the container's own transform then places the whole thing.
  mesh.setOrtho(mesh.width, mesh.height);
  mesh.addVertices(
    [tl[0], -tl[1], tr[0], -tr[1], br[0], -br[1], bl[0], -bl[1]],
    [0, 0, 1, 0, 1, 1, 0, 1],
    [0, 1, 2, 0, 2, 3],
  );
  container.add(mesh);
  // The texture is one-per-sprite, so it has to go when the sprite does or the manager fills
  // up with dead plates as cars are drawn and redrawn.
  container.once('destroy', () => scene.textures?.remove?.(key));
  return mesh;
}

function addPlate(scene, container, img, model, plate) {
  if (!plate) return null;
  const scale = img.displayHeight / (img.height || 1);
  return addPlateMesh(scene, container, img, model, plate, scale)
    ?? addPlateFlat(scene, container, img, model, plate, scale);
}

// Give a car container a hit area over its visible body — and ONLY this shape. A sized
// container (setSize + default 0.5 origin) is hit-tested by Phaser in top-left-anchored
// local space, so the rectangle must be (0, 0, w, h); the centred (-w/2, -h/2) rectangle
// every caller used to build gets the origin shift applied twice and lands the clickable
// zone half a car to the LEFT and ABOVE the bodywork. Call again after a re-fit (lane
// change) to refresh the rectangle in place without re-adding handlers.
export function fitCarHitArea(container, options) {
  const w = container.img?.displayWidth ?? 300;
  const h = container.img?.displayHeight ?? 150;
  if (container.input?.hitArea) container.input.hitArea.setTo(0, 0, w, h);
  else container.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains, options);
  return container;
}

// The people seen through the side windows: `driver_<character>` sprites (head and
// shoulders, drawn facing LEFT like the car art), placed UNDER the body image so the
// bodywork masks everything but what the see-through windows show, and flipped with the car
// (see setCarFacing). Occupant 0 sits at the driver's window, the rest at the rear one, each
// a little further back. Sized against the BODY, not the window — a head is the same size
// in a Panda and a Tesla, the Tesla's low window just shows less of it — with the eye line
// (26 % down the sprite) at 66 % of the window's height and 66 % of its width, calibrated
// 2026-09-03 across all seven models (foreheads clear of the roof line, driver behind the
// A-pillar, not on it).
const OCCUPANT_H_PER_BODY = 0.46;   // sprite height / body height
const OCCUPANT_EYE = 0.26;          // where the eye line is down the sprite (its origin y)
const OCCUPANT_FX = 0.66;           // head centre across the window
const OCCUPANT_FY = 0.66;           // eye line down the window
const OCCUPANT_REAR_STEP = 22;      // source px each extra passenger sits behind the last

function seatFor(model, index) {
  const seats = CAR_SEATS[model];
  if (!seats) return null;
  if (index === 0) return seats.driver;
  const r = seats.rear;
  const dx = (index - 1) * OCCUPANT_REAR_STEP;
  return { x0: r.x0 + dx, x1: r.x1 + dx, y0: r.y0, y1: r.y1 };
}

// Size and place every occupant for the body's CURRENT display size (called again whenever
// the body is re-fitted — a lane change scales the whole car).
function fitOccupants(container) {
  const img = container.img;
  if (!img || !container.occupants?.length) return;
  const scale = img.displayHeight / (img.height || 1);
  const ox = -img.displayWidth / 2, oy = -img.displayHeight / 2;
  const bodyH = img.displayHeight * (CAR_CONTENT_FRAC.side[container.carModel] ?? 0.8);
  const h = bodyH * OCCUPANT_H_PER_BODY;
  const flipped = container.carFacing === 'right';
  container.occupants.forEach((d, i) => {
    const seat = seatFor(container.carModel, i);
    if (!seat) { d.setVisible(false); return; }
    const cx = ox + (seat.x0 + OCCUPANT_FX * (seat.x1 - seat.x0)) * scale;
    const cy = oy + (seat.y0 + OCCUPANT_FY * (seat.y1 - seat.y0)) * scale;
    d.setDisplaySize(h * (d.width / d.height || 0.7), h);
    d.setFlipX(flipped);
    d.x = flipped ? -cx : cx;
    d.y = cy;
    d.setVisible(true);
  });
}

/**
 * Who is in the car, driver first: character ids (`'natalka'`, `'ken'`…) whose
 * `driver_<id>` texture exists (or their hairstyle's own, look/hairArt driverKey). Side view only; replaces the
 * previous crew. A style's picture not in yet (another player's) is today's until it lands, then swapped in.
 */
export function setCarOccupants(container, characters = [], { looks = [] } = {}) {
  if (!container || container.carView !== 'side' || !container.img) return;
  const scene = container.scene;
  for (const d of container.occupants ?? []) d.destroy();
  container.occupants = [];
  const crew = {};
  container._crew = crew;
  container._crewOf = { characters, looks };
  characters.forEach((ch, i) => {
    if (!ch) return;
    // the style's own picture when it has driver art and it is in (a one-sided style's own right-facing one in a car
    // facing right, else its left one mirrored); today's (the default style) otherwise
    const want = driverKey(ch, looks[i], HAIR_ART, container.carFacing === 'right');
    const key = [want, driverKey(ch, looks[i]), `driver_${ch}`].find((k) => scene.textures.exists(k));
    if (key !== want) {
      preloader.whenReady(want, () => { if (container.active && container._crew === crew) setCarOccupants(container, characters, { looks }); });
    }
    if (!key) return;
    const d = scene.add.image(0, 0, key).setOrigin(0.5, OCCUPANT_EYE);
    // their look (look/): the mask loads only for a look that needs one
    if (!isDefaultLook(ch, looks[i])) ensureMasks(scene, [key], () => { if (d.active) applyLook(d, ch, looks[i]); });
    // Behind the body, and each passenger behind the one in front of them.
    container.addAt(d, 0);
    container.occupants.push(d);
  });
  fitOccupants(container);
}

export function createCarSprite(scene, model, view = 'side', x = 0, y = 0,
  { plate, wheelY = null, driver = null, occupants = null, looks = [] } = {}) {
  const container = scene.add.container(x, y);
  container.carModel = model;
  container.carView = view;
  // Side view only: the ground line the body is sized against, and the distance from the
  // container's centre down to the wheels at that size (what the scenes y-sort and place by).
  container.groundY = view === 'side' ? wheelY : null;
  container.wheelOffset = view === 'side' ? carWheelOffset(model, wheelY ?? undefined) : 0;

  const key = `car_${model}_${view}`;
  const h = view === 'side' ? carDisplayHeightAt(model, wheelY ?? undefined)
    : carDisplayHeight(model, view);

  if (scene.textures.exists(key)) {
    const img = scene.add.image(0, 0, key);
    const src = scene.textures.get(key).getSourceImage();
    container.carAspect = src.width / src.height;
    img.setDisplaySize(h * container.carAspect, h);
    container.add(img);
    container.img = img;
    container.plateText = view === 'front' ? addPlate(scene, container, img, model, plate) : null;
    if (view === 'side') setCarOccupants(container, occupants ?? (driver ? [driver] : []), { looks });
  } else {
    // Missing art must not take the scene down with it — a grey box with the model name
    // is enough to keep driving/parking playable (and obvious in a screenshot).
    const rect = scene.add.rectangle(0, 0, FALLBACK_W, FALLBACK_H, FALLBACK_COLOR);
    const label = scene.add.text(0, 0, model, {
      fontFamily: 'system-ui', fontSize: '20px', color: '#222',
    }).setOrigin(0.5);
    container.add([rect, label]);
    container.img = rect;      // setCarFacing tolerates a shape (nothing to flip)
    container.carAspect = FALLBACK_W / FALLBACK_H;
    container.plateText = null;
  }

  container.setSize(container.img.displayWidth, container.img.displayHeight);
  // `wheelY` wins over the `y` argument: the ground line is the truth, and how far the
  // centre sits above it depends on the size we just picked for this line.
  if (view === 'side' && Number.isFinite(wheelY)) container.y = wheelY - container.wheelOffset;
  return container;
}

/**
 * Put a side-view car's WHEELS on the ground line `wheelY`, drawn at that line's perspective
 * size (the near lane is bigger than the far one, a parked car bigger than one up the road).
 * Moves the container's y with it — the body scales about its centre, so the centre has to
 * move by more than the ground line did. No-op on the front portrait, which is indoor
 * furniture and has no ground line.
 */
export function setCarGroundY(container, wheelY) {
  if (!container || container.carView !== 'side' || !Number.isFinite(wheelY)) return;
  const h = carDisplayHeightAt(container.carModel, wheelY);
  container.img?.setDisplaySize?.(h * (container.carAspect || 2), h);
  fitOccupants(container);
  container.groundY = wheelY;
  container.wheelOffset = carWheelOffset(container.carModel, wheelY);
  container.y = wheelY - container.wheelOffset;
  container.setSize(container.img.displayWidth, container.img.displayHeight);
}

// Which way the car points. Only the side view has a direction; the front portrait
// is symmetric and must never be mirrored (it would mirror the plate with it).
export function setCarFacing(container, facing) {
  if (!container || container.carView !== 'side') return;
  const was = container.carFacing;
  container.carFacing = facing === 'left' ? 'left' : 'right';
  container.img?.setFlipX?.(container.carFacing === 'right');
  // a one-sided style's crew changes picture with the side it shows
  const crew = container._crewOf;
  if (was !== container.carFacing && crew?.characters.some((ch, i) => ch && driverKey(ch, crew.looks[i], HAIR_ART, true) !== driverKey(ch, crew.looks[i]))) {
    setCarOccupants(container, crew.characters, crew);
    return;
  }
  fitOccupants(container);   // the crew mirrors with the body
}

export function setCarDepth(container, depth) {
  container?.setDepth(depth);
}
